/**
 * assets/gc-cart.js – GC cart drawer engine.
 *
 * Liquid (sections/gc-cart-drawer.liquid) computes every number. This module only:
 *  - sends cart mutations with sections=gc-cart-drawer (one round trip) and morphs the
 *    [data-gcc-part] regions back in with Horizon's morph(), so nodes, focus and CSS
 *    transitions survive;
 *  - paints quantity changes optimistically and merges rapid taps into one request;
 *  - opens the drawer for every add-to-cart path on the site (native forms, Horizon
 *    events, the legacy cart:updated event, and a PerformanceObserver safety net);
 *  - runs the "3 of the same" offer: applies the bundle code when one set reaches the
 *    bundle quantity and removes only the code it applied itself;
 *  - shows a processing state on Checkout until the browser leaves for checkout.
 */
import { morph } from '@theme/morph';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SECTION = 'gc-cart-drawer';
const MARK = 'gcc=1';
const STEP_DELAY = 200;
const BUNDLE_MARK = 'gcc_bundle';
const GIFT_DECLINED = 'gcc_gift_declined';
const GIFT_PROPERTY = '_gc_gift';
const SETS = '.gcc-line:not(.gcc-line--ghost):not(.gcc-line--gift)';
// Gift cards count in the header and the total, but never toward a tier, the bundle or the gift.
const GLASS = `${SETS}:not([data-gift-card="true"])`;
const root = () => window.Shopify?.routes?.root || '/';
const url = (path) => `${root()}${path}${path.includes('?') ? '&' : '?'}${MARK}`;
const NETWORK_MESSAGE = 'Connection hiccup. Please try again.';
const friendly = (message) =>
  !message || /failed to fetch|load failed|networkerror|network request failed/i.test(message) ? NETWORK_MESSAGE : message;
const isNetworkError = (error) => error instanceof TypeError || error?.name === 'TypeError';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isPhone = () => window.matchMedia('(max-width: 749px)').matches;

class GCCartDrawer extends HTMLElement {
  #queue = Promise.resolve();
  #timers = new Map();
  #pendingQty = new Map();
  #refreshing = null;
  #recsCache = new Map();
  #history = false;
  #fmt = null;
  #removing = new Set();
  #bundleBusy = false;
  #giftBusy = false;
  #checkoutTimer = 0;
  #pending = 0;
  #stepTimer = 0;
  #busyText = '';
  #dirty = false;
  #lastOwnUpdate = 0;
  #abort = null;

  connectedCallback() {
    this.dialog = this.querySelector('[data-gcc-dialog]');
    this.scroller = this.querySelector('[data-gcc-scroll]');
    if (!this.dialog) return;
    this.#abort?.abort();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;

    if (history.state?.gcc) history.replaceState({ ...history.state, gcc: false }, '');

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onCheckoutSubmit, { signal });
    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal }
    );
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );
    this.dialog.addEventListener('close', this.#onNativeClose, { signal });

    document.addEventListener('click', this.#onOpenerClick, { capture: true, signal });
    document.addEventListener('submit', this.#onAddFormSubmit, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onThemeCartUpdate, { signal });
    document.addEventListener('cart:updated', this.#onLegacyCartUpdated, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });
    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted) return;
        this.#setCheckoutBusy(false);
        this.refresh();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close();
      },
      { signal }
    );

    this.#watchNetwork(signal);
    if (Number(this.dataset.count) > 0) this.#syncGift();
    this.#warmOnIntent();

    window.GCCart = {
      open: () => this.open(),
      close: () => this.close(),
      refresh: (options) => this.refresh(options),
      add: (items, options) => this.add(items, options),
      change: (key, quantity) => this.change(key, quantity),
    };
    document.dispatchEvent(new CustomEvent('gc-cart:ready'));
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /* ---------- open / close ---------- */

  get isOpen() {
    return this.dialog.open;
  }

  open() {
    if (this.isOpen) {
      this.dialog.classList.add('is-open');
      return;
    }
    this.#paintDelivery();
    this.#eagerImages();
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    if (isPhone() && !this.#history) {
      history.pushState({ ...(history.state || {}), gcc: true }, '');
      this.#history = true;
    }
    this.#loadAiUpsells();
  }

  close({ fromHistory = false } = {}) {
    if (!this.isOpen) return;
    this.dialog.classList.remove('is-open');
    const done = () => {
      if (this.dialog.classList.contains('is-open')) return;
      this.dialog.close();
      document.documentElement.style.overflow = '';
    };
    if (reduceMotion()) done();
    else setTimeout(done, 320);
    if (this.#history) {
      this.#history = false;
      if (!fromHistory && history.state?.gcc) history.back();
    }
  }

  /** Esc that cannot be cancelled, Android Back via the close watcher, or any other native close. */
  #onNativeClose = () => {
    this.dialog.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    if (this.#history) {
      this.#history = false;
      if (history.state?.gcc) history.back();
    }
  };

  #onPopState = () => {
    if (this.#history && this.isOpen) {
      this.#history = false;
      this.close({ fromHistory: true });
    }
  };

  #onOpenerClick = (event) => {
    const opener = event.target.closest?.('[data-gcc-open]');
    if (!opener || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    this.open();
  };

  /* ---------- clicks inside the drawer ---------- */

  #onClick = (event) => {
    const target = event.target;
    if (target.closest('[data-gcc-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    if (this.dataset.checkout === 'busy') return;
    const step = target.closest('[data-gcc-step]');
    if (step && !step.disabled) {
      this.#step(step.closest('.gcc-line'), Number(step.dataset.gccStep));
      return;
    }
    if (target.closest('[data-gcc-remove]')) {
      const line = target.closest('.gcc-line');
      if (line?.dataset.gift === 'true') this.#giftDeclined(true);
      this.#remove(line);
      return;
    }
    const add = target.closest('[data-gcc-add]');
    if (add && !add.classList.contains('is-busy')) {
      this.#upsellAdd(add);
      return;
    }
    const bundle = target.closest('[data-gcc-bundle]');
    if (bundle && !bundle.classList.contains('is-busy')) this.#bundleAdd(bundle);
  };

  /* ---------- quantity: optimistic, merged ---------- */

  #step(line, delta) {
    if (!line) return;
    const key = line.dataset.key;
    const current = this.#pendingQty.get(key) ?? Number(line.dataset.qty);
    const max = line.dataset.max ? Number(line.dataset.max) : Infinity;
    let next = Math.max(0, current + delta);
    if (next > max) {
      next = max;
      this.#lineError(line, `Only ${max} in stock.`);
    }
    if (next === current) return;
    if (next === 0) {
      this.#remove(line);
      return;
    }
    this.#pendingQty.set(key, next);
    this.#paintLine(line, next);
    clearTimeout(this.#timers.get(key));
    this.#timers.set(
      key,
      setTimeout(() => {
        this.#timers.delete(key);
        const quantity = this.#pendingQty.get(key);
        this.change(key, quantity)
          .catch(() => {})
          .finally(() => {
            if (this.#pendingQty.get(key) === quantity) this.#pendingQty.delete(key);
          });
      }, STEP_DELAY)
    );
  }

  #paintLine(line, qty) {
    const n = line.querySelector('[data-gcc-qty]');
    if (n) n.textContent = String(qty);
    const now = line.querySelector('[data-gcc-line-now]');
    const unit = Number(line.dataset.unit);
    if (now && Number.isFinite(unit)) now.textContent = unit * qty === 0 ? 'Free' : this.#money(unit * qty);
    const was = line.querySelector('.gcc-line__was');
    if (was) was.hidden = true;
    const plus = line.querySelector('[data-gcc-step="1"]');
    if (plus && line.dataset.max) plus.disabled = qty >= Number(line.dataset.max);
    line.classList.add('is-busy');
    this.#paintTotals();
  }

  /**
   * Header count and footer total follow taps at once (line prices use the undiscounted unit
   * price); the server render settles them a moment later.
   */
  #paintTotals() {
    let units = 0;
    let cents = 0;
    let glassCents = 0;
    for (const line of this.querySelectorAll(SETS)) {
      const key = line.dataset.key;
      const server = Number(line.dataset.qty) || 0;
      const want = this.#removing.has(key) ? 0 : (this.#pendingQty.get(key) ?? server);
      const delta = (want - server) * (Number(line.dataset.unit) || 0);
      units += want - server;
      cents += delta;
      if (line.dataset.giftCard !== 'true') glassCents += delta;
    }
    const count = this.querySelector('[data-gcc-units]');
    if (count) {
      const n = Math.max(0, Number(count.dataset.n) + units);
      count.textContent = `${n} ${n === 1 ? this.dataset.itemSingular : this.dataset.itemPlural}`;
    }
    const total = this.querySelector('[data-gcc-total]');
    if (!total) return;
    const changed = units !== 0;
    // A discount may switch on or off with this change (one already applies, or the tap crosses the
    // percent tier): dim until Shopify answers instead of showing a guess.
    const threshold = Number(total.dataset.pctThr) || 0;
    const subtotal = Number(total.dataset.sub) || 0;
    const crosses = threshold > 0 && subtotal < threshold !== subtotal + glassCents < threshold;
    const need = Number(this.dataset.bundleQty) || 3;
    const bundleCross =
      this.dataset.bundleOn === 'true' &&
      [...this.querySelectorAll(GLASS)].some((line) => {
        const server = Number(line.dataset.qty) || 0;
        const want = this.#removing.has(line.dataset.key) ? 0 : (this.#pendingQty.get(line.dataset.key) ?? server);
        return server < need !== want < need;
      });
    const unsure = changed && (total.dataset.discounted === 'true' || crosses || bundleCross);
    total.classList.toggle('is-pending', unsure);
    this.querySelector('[data-gcc-saving]')?.classList.toggle('is-pending', unsure);
    if (!unsure) total.textContent = this.#money(Math.max(0, Number(total.dataset.cents) + cents));
  }

  #remove(line) {
    if (!line) return;
    const key = line.dataset.key;
    clearTimeout(this.#timers.get(key));
    this.#timers.delete(key);
    this.#pendingQty.delete(key);
    if (!reduceMotion()) {
      line.style.maxHeight = `${line.offsetHeight}px`;
      line.classList.add('is-leaving');
    }
    const hadFocus = line.contains(document.activeElement);
    const next = line.nextElementSibling?.dataset.key || line.previousElementSibling?.dataset.key;
    this.#removing.add(key);
    this.#paintTotals();
    this.change(key, 0)
      .then(() => {
        if (!hadFocus) return;
        const target = next && this.querySelector(`.gcc-line[data-key="${CSS.escape(next)}"] [data-gcc-remove]`);
        (target || this.querySelector('.gcc__close'))?.focus();
      })
      .catch(() => {
        line.classList.remove('is-leaving');
        line.style.maxHeight = '';
      })
      .finally(() => {
        this.#removing.delete(key);
        this.#paintTotals();
      });
  }

  #lineError(line, message) {
    const el = line?.querySelector('[data-gcc-line-error]');
    if (!el) return;
    el.textContent = friendly(message);
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.hidden = true), 4000);
  }

  /* ---------- cart API (serialised) ---------- */

  #enqueue(task) {
    this.#pending += 1;
    const run = this.#queue.then(task, task);
    this.#queue = run.catch(() => {}).finally(() => (this.#pending -= 1));
    return run;
  }

  /** True while a cart request is queued or in flight. */
  get #busyQueue() {
    return this.#pending > 0;
  }

  /** Quantity and discount updates set absolute values, so one retry after a dropped connection is safe. */
  async #post(path, body) {
    try {
      return await this.#postOnce(path, body);
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      if (!/cart\/(change|update)\.js/.test(path)) throw error;
      await wait(500);
      try {
        return await this.#postOnce(path, body);
      } catch (retryError) {
        throw isNetworkError(retryError) ? new Error(NETWORK_MESSAGE) : retryError;
      }
    }
  }

  async #postOnce(path, body) {
    const isForm = body instanceof FormData;
    if (isForm) body = this.#cloneForm(body);
    if (isForm) {
      body.set('sections', SECTION);
      body.set('sections_url', window.location.pathname);
    } else {
      body = JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname });
    }
    const response = await fetch(url(path), {
      method: 'POST',
      headers: isForm
        ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        : { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || 'Something went wrong. Please try again.');
      error.data = data;
      throw error;
    }
    return data;
  }

  /**
   * When a quantity change crosses the bundle quantity, the code goes in the same request
   * (update.js), so the cart and the code never disagree, not even for a moment.
   */
  #bundlePlan(key, quantity) {
    const code = (this.dataset.bundleCode || '').trim();
    if (!code || this.dataset.bundleOn !== 'true') return null;
    const need = Number(this.dataset.bundleQty) || 3;
    const lines = [...this.querySelectorAll(GLASS)];
    const qtyOf = (line) => (line.dataset.key === key ? quantity : Number(line.dataset.qty) || 0);
    const qualifies = lines.some((line) => line.dataset.bundleOk === 'true' && qtyOf(line) >= need);
    const applied = (this.querySelector('[data-codes]')?.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
    const has = applied.some((c) => c.toUpperCase() === code.toUpperCase());
    const others = this.#otherCodes(applied);
    if (has && this.#bundleMine() && !qualifies) return { discount: others.join(','), mine: false };
    if (!has && qualifies && quantity >= need && lines.filter((line) => qtyOf(line) > 0).length === 1) {
      return { discount: [...others, code].join(','), mine: true };
    }
    return null;
  }

  /**
   * A row can stand for several cart lines (Shopify splits a line when a discount covers only some
   * units). The row's quantity goes on its first line and the others are emptied, in one request.
   */
  #updatesFor(key, quantity) {
    const line = this.querySelector(`.gcc-line[data-key="${CSS.escape(key)}"]`);
    const keys = (line?.dataset.keys || key).split(',').filter(Boolean);
    const updates = {};
    keys.forEach((k, i) => (updates[k] = i === 0 ? quantity : 0));
    return { updates, split: keys.length > 1 };
  }

  change(key, quantity) {
    let cart = null;
    return this.#enqueue(async () => {
      try {
        const plan = this.#bundlePlan(key, quantity);
        const { updates, split } = this.#updatesFor(key, quantity);
        if (plan) {
          const codes = await this.#cartCodes();
          if (codes) {
            const code = this.dataset.bundleCode.trim();
            plan.discount = (plan.mine ? [...this.#otherCodes(codes), code] : this.#otherCodes(codes)).join(',');
          }
        }
        const data =
          plan || split
            ? await this.#post('cart/update.js', plan ? { updates, discount: plan.discount } : { updates })
            : await this.#post('cart/change.js', { id: key, quantity });
        if (plan) this.#bundleMine(plan.mine);
        const { sections, ...rest } = data;
        cart = rest;
        this.#apply(sections?.[SECTION], { source: 'change', cart });
        this.#announce(quantity === 0 ? 'Item removed.' : 'Cart updated.');
        return data;
      } catch (error) {
        await this.#refreshNow().catch(() => {});
        const line = this.querySelector(`.gcc-line[data-key="${CSS.escape(key)}"]`);
        if (line) this.#lineError(line, error.message);
        else this.#notice(error.message);
        throw error;
      }
    }).then((data) => {
      this.#syncAll(cart, { fromStep: quantity > 0 });
      return data;
    });
  }

  #cloneForm(form) {
    const copy = new FormData();
    for (const [key, value] of form.entries()) copy.append(key, value);
    return copy;
  }

  #variantQty(variantId) {
    let qty = 0;
    for (const line of this.querySelectorAll('.gcc-line:not(.gcc-line--ghost)')) {
      if (line.dataset.key?.split(':')[0] === String(variantId)) qty += Number(line.dataset.qty) || 0;
    }
    return qty;
  }

  /**
   * Adds are not safe to repeat blindly: a dropped connection may still have reached Shopify.
   * After a network failure, re-read the cart; if the item arrived, that is success, else try once more.
   */
  async #addRecovering(body, variantId) {
    const before = this.#variantQty(variantId);
    try {
      return await this.#postOnce('cart/add.js', body);
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      await wait(400);
      await this.#refreshNow().catch(() => {});
      if (variantId && this.#variantQty(variantId) > before) return { sections: null, recovered: true };
      try {
        return await this.#postOnce('cart/add.js', body);
      } catch (retryError) {
        throw isNetworkError(retryError) ? new Error(NETWORK_MESSAGE) : retryError;
      }
    }
  }

  add(items, { source = 'gc-cart', open = true } = {}) {
    if (open) this.#showAdding();
    return this.#enqueue(async () => {
      try {
        const data = await this.#addRecovering({ items }, items?.[0]?.id);
        if (data.sections) this.#apply(data.sections[SECTION], { source: 'add' });
        if (open) this.open();
        this.#announce('Added to cart.');
        return data;
      } catch (error) {
        this.#clearAdding();
        this.#notice(error.message);
        if (open) this.open();
        throw error;
      }
    }).then((data) => {
      this.#syncAll(null);
      return data;
    });
  }

  async #addForm(form, submitter) {
    const body = new FormData(form);
    submitter?.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;
    this.#showAdding();
    try {
      await this.#enqueue(async () => {
        const data = await this.#addRecovering(body, body.get('id'));
        if (data.sections) this.#apply(data.sections[SECTION], { source: 'add' });
      });
      this.open();
      this.#announce('Added to cart.');
      this.#syncAll(null);
    } catch (error) {
      this.#clearAdding();
      this.#notice(error.message);
      this.open();
    } finally {
      submitter?.removeAttribute('aria-busy');
      if (submitter) submitter.disabled = false;
    }
  }

  /** Re-render the drawer from the server. Calls within one burst share one request. */
  refresh({ open = false } = {}) {
    if (this.#refreshing) {
      this.#dirty = true;
    } else {
      this.#refreshing = new Promise((resolve) => setTimeout(resolve, 60))
        .then(async () => {
          do {
            this.#dirty = false;
            await this.#enqueue(() => this.#refreshNow());
          } while (this.#dirty);
        })
        .finally(() => (this.#refreshing = null));
    }
    return this.#refreshing.then(() => {
      if (open) this.open();
      this.#syncAll(null);
    });
  }

  async #refreshNow() {
    const response = await fetch(url(`?sections=${SECTION}`), { headers: { Accept: 'application/json' } }).catch(async () => {
      await wait(500);
      return fetch(url(`?sections=${SECTION}`), { headers: { Accept: 'application/json' } });
    });
    const data = await response.json().catch(() => ({}));
    this.#apply(data[SECTION], { source: 'refresh' });
  }

  /* ---------- render ---------- */

  #apply(html, { source, cart = null } = {}) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('gc-cart-drawer');
    if (!fresh) return;

    const before = new Set([...this.querySelectorAll('.gcc-line')].map((line) => line.dataset.key));
    for (const part of fresh.querySelectorAll('[data-gcc-part]')) {
      const current = this.querySelector(`[data-gcc-part="${part.dataset.gccPart}"]`);
      if (current) morph(current, part);
    }
    this.dataset.count = fresh.dataset.count;
    this.dataset.moneySample = fresh.dataset.moneySample;
    this.#fmt = null;

    for (const [key, qty] of this.#pendingQty) {
      const line = this.querySelector(`.gcc-line[data-key="${CSS.escape(key)}"]`);
      if (line && (this.#timers.has(key) || line.dataset.qty !== String(qty))) this.#paintLine(line, qty);
    }
    if (source === 'add') {
      for (const line of this.querySelectorAll('.gcc-line')) {
        if (!before.has(line.dataset.key)) {
          line.classList.add('is-new');
          line.addEventListener('animationend', () => line.classList.remove('is-new'), { once: true });
        }
      }
    }

    this.#paintTotals();
    this.#paintDelivery();
    if (this.dataset.checkout === 'busy') {
      const button = this.querySelector('[data-gcc-checkout]');
      button?.setAttribute('aria-busy', 'true');
      button?.setAttribute('aria-disabled', 'true');
      const text = this.querySelector('[data-gcc-busy-text]');
      if (text && this.#busyText) text.textContent = this.#busyText;
    }
    if (this.isOpen) {
      this.#eagerImages();
      this.#loadAiUpsells();
    }

    this.#lastOwnUpdate = Date.now();
    this.#announceCart(cart);
  }

  /** Tell the header and Horizon's product forms. They need the cart items, so hand them over (one /cart.js at most). */
  async #announceCart(cart) {
    const itemCount = Number(this.dataset.count) || 0;
    if (!cart?.items && document.querySelector('product-form-component')) {
      cart = await fetch(url('cart.js'))
        .then((response) => response.json())
        .catch(() => null);
    }
    document.dispatchEvent(
      new CartUpdateEvent(cart?.items ? cart : { item_count: itemCount }, 'gc-cart', { source: 'gc-cart', itemCount })
    );
  }

  /**
   * Checkout shows its processing state on the first press, ignores further presses, sends any
   * quantity tap still in its merge window, then goes straight to /checkout (one hop fewer than
   * posting the cart form; the form stays as the no-JavaScript fallback).
   */
  #onCheckoutSubmit = async (event) => {
    const form = event.target.closest?.('[data-gcc-form]');
    if (!form || form.dataset.flushed === 'true') return;
    event.preventDefault();
    if (this.dataset.checkout === 'busy') return;
    const messagesShown = this.#setCheckoutBusy(true);
    for (const key of [...this.#timers.keys()]) {
      clearTimeout(this.#timers.get(key));
      this.#timers.delete(key);
      const quantity = this.#pendingQty.get(key);
      if (quantity !== undefined) this.change(key, quantity).catch(() => {});
    }
    if (this.#busyQueue) {
      let queue;
      do {
        queue = this.#queue;
        await queue;
        await Promise.resolve();
      } while (queue !== this.#queue);
    }
    this.#pendingQty.clear();
    if (Number(this.dataset.count) === 0) {
      this.#setCheckoutBusy(false);
      return;
    }
    // A moment of anticipation: every message is seen once, then checkout opens.
    await messagesShown;
    if (this.dataset.checkout !== 'busy') return;
    window.location.assign(`${root()}checkout`);
  };

  #setCheckoutBusy(busy) {
    clearTimeout(this.#checkoutTimer);
    clearInterval(this.#stepTimer);
    const button = this.querySelector('[data-gcc-checkout]');
    const layer = this.querySelector('.gcc-checkout__busy');
    const text = layer?.querySelector('[data-gcc-busy-text]');
    const steps = (layer?.dataset.steps || '').split('|').map((step) => step.trim()).filter(Boolean);
    if (text && steps.length) text.textContent = steps[0];
    this.#busyText = busy ? steps[0] || '' : '';
    if (busy) {
      this.dataset.checkout = 'busy';
      button?.setAttribute('aria-busy', 'true');
      button?.setAttribute('aria-disabled', 'true');
      this.#announce('Taking you to checkout.');
      // If the browser has not left after 20 s (offline, blocked), let the shopper try again.
      this.#checkoutTimer = setTimeout(() => this.#setCheckoutBusy(false), 20000);
      // Reassuring steps, one after another; resolves as the last one appears (it stays while checkout loads).
      const stepMs = Number(layer?.dataset.stepMs) || 900;
      let index = 0;
      return new Promise((resolve) => {
        if (!text || steps.length < 2) return setTimeout(resolve, stepMs);
        this.#stepTimer = setInterval(() => {
          index += 1;
          const last = index >= steps.length - 1;
          if (last) clearInterval(this.#stepTimer);
          const show = () => {
            this.#busyText = steps[Math.min(index, steps.length - 1)];
            text.textContent = this.#busyText;
            text.classList.remove('is-swap');
            if (last) resolve();
          };
          if (reduceMotion()) return show();
          text.classList.add('is-swap');
          setTimeout(show, 170);
        }, stepMs);
      });
    } else {
      delete this.dataset.checkout;
      button?.removeAttribute('aria-busy');
      button?.removeAttribute('aria-disabled');
      const form = this.querySelector('[data-gcc-form]');
      if (form) delete form.dataset.flushed;
    }
  }

  /* ---------- buy 3 of the same ---------- */

  #bundleMine(value) {
    try {
      if (value === true) localStorage.setItem(BUNDLE_MARK, '1');
      else if (value === false) localStorage.removeItem(BUNDLE_MARK);
      return localStorage.getItem(BUNDLE_MARK) === '1';
    } catch (error) {
      return false;
    }
  }

  /** Every code on the cart, applicable or not (the rendered list only has the ones that apply now). */
  async #cartCodes() {
    const cart = await fetch(url('cart.js'))
      .then((response) => response.json())
      .catch(() => null);
    return cart?.discount_codes ? cart.discount_codes.map((entry) => entry.code) : null;
  }

  #otherCodes(codes) {
    const own = (this.dataset.bundleCode || '').toUpperCase();
    return codes.map((code) => String(code).trim()).filter((code) => code && code.toUpperCase() !== own);
  }

  async #bundleAdd(button) {
    const code = (this.dataset.bundleCode || '').trim();
    const keys = (button.dataset.gccBundle || '').split(',').filter(Boolean);
    const quantity = Number(button.dataset.gccBundleQty) || Number(this.dataset.bundleQty) || 3;
    if (!code || !keys.length) return;
    const updates = {};
    keys.forEach((k, i) => (updates[k] = i === 0 ? quantity : 0));
    // A stepper tap still in its merge window would undo the bundle a moment later.
    for (const k of keys) {
      clearTimeout(this.#timers.get(k));
      this.#timers.delete(k);
      this.#pendingQty.delete(k);
    }
    let applied = (this.querySelector('[data-codes]')?.dataset.codes || '').split(',');
    button.classList.add('is-busy');
    this.#bundleBusy = true;
    try {
      await this.#enqueue(async () => {
        applied = (await this.#cartCodes()) || applied;
        const data = await this.#post('cart/update.js', {
          updates,
          discount: [...this.#otherCodes(applied), code].join(','),
        });
        const { sections, ...cart } = data;
        this.#bundleMine(true);
        this.#apply(sections?.[SECTION], { source: 'change', cart });
      });
      this.#announce('Added. Your bundle discount is applied.');
      window.clarity?.('event', 'gc_cart_bundle_add');
    } catch (error) {
      this.#notice(error.message);
      this.refresh();
    } finally {
      this.#bundleBusy = false;
      button.classList.remove('is-busy');
    }
  }

  /**
   * Keeps the bundle code honest: applied when the only set in the cart reaches the bundle
   * quantity through the drawer, removed (only if the drawer applied it) once no set qualifies.
   * Codes the shopper or the product page applied are never touched.
   */
  async #syncBundle(cart, { fromStep = false } = {}) {
    if (this.#bundleBusy || this.dataset.bundleOn !== 'true') return;
    const code = (this.dataset.bundleCode || '').trim();
    const need = Number(this.dataset.bundleQty) || 3;
    if (!code) return;
    const mine = this.#bundleMine();
    if (!cart?.items) {
      if (!mine) return;
      cart = await fetch(url('cart.js'))
        .then((response) => response.json())
        .catch(() => null);
      if (!cart?.items) return;
    }
    const codes = (cart.discount_codes || []).map((entry) => entry.code);
    const has = codes.some((entry) => entry.toUpperCase() === code.toUpperCase());
    const eligible = new Set(
      [...this.querySelectorAll(`${GLASS}[data-bundle-ok="true"]`)].map((line) => line.dataset.variantId)
    );
    const sets = cart.items.filter((item) => !item.properties?.[GIFT_PROPERTY] && !item.gift_card);
    const perVariant = new Map();
    for (const item of sets) perVariant.set(String(item.variant_id), (perVariant.get(String(item.variant_id)) || 0) + item.quantity);
    const qualifies = [...perVariant].some(([variant, qty]) => qty >= need && eligible.has(variant));
    let discount = null;
    if (has && mine && !qualifies) discount = this.#otherCodes(codes).join(',');
    else if (!has && qualifies && fromStep && perVariant.size === 1) discount = [...this.#otherCodes(codes), code].join(',');
    if (!has && mine && !qualifies) this.#bundleMine(false);
    if (discount === null) return;

    this.#bundleBusy = true;
    try {
      await this.#enqueue(async () => {
        const data = await this.#post('cart/update.js', { discount });
        const { sections, ...fresh } = data;
        this.#bundleMine(discount.toUpperCase().split(',').includes(code.toUpperCase()));
        this.#apply(sections?.[SECTION], { source: 'change', cart: fresh });
      });
    } catch (error) {
      /* the cart still renders correctly; the code just stays as it was */
    } finally {
      this.#bundleBusy = false;
    }
  }

  /* ---------- free gift with any set ---------- */

  /** A removed gift stays removed in every tab for 12 hours. */
  #giftDeclined(value) {
    try {
      if (value === true) localStorage.setItem(GIFT_DECLINED, String(Date.now()));
      const at = Number(localStorage.getItem(GIFT_DECLINED)) || 0;
      return at > 0 && Date.now() - at < 12 * 3600 * 1000;
    } catch (error) {
      return value === true;
    }
  }

  async #syncAll(cart, options) {
    await this.#syncGift();
    await this.#syncBundle(cart, options);
  }

  /**
   * One gift while a qualifying set is in the cart: added when the first set arrives, removed
   * when the last set leaves, never re-added after the shopper removes it, and removed if
   * Shopify does not make it free (the discount and the qualifying sets must match).
   */
  async #syncGift() {
    if (this.#giftBusy) return;
    const variant = this.dataset.giftVariant;
    const ends = Number(this.dataset.giftEnds) || 0;
    const live = this.dataset.giftOn === 'true' && (!ends || Date.now() < ends * 1000);
    const all = [...this.querySelectorAll('.gcc-line:not(.gcc-line--ghost)')];
    const gifts = all.filter((line) => line.dataset.gift === 'true');
    const qualifies = all.some((line) => line.dataset.giftOk === 'true' && Number(line.dataset.qty) > 0);
    const hasGiftProduct = all.some((line) => line.dataset.productId === this.dataset.giftProduct);
    const unpaid = gifts.some((line) => Number(line.dataset.final) > 0);

    let body = null;
    let path = 'cart/update.js';
    const doubled = gifts.length > 1 || gifts.some((line) => Number(line.dataset.qty) > 1);
    if (doubled && qualifies && live) {
      // Two tabs added it at once: keep one gift, which the discount covers.
      const updates = {};
      gifts.forEach((line, i) => line.dataset.keys.split(',').forEach((key, j) => (updates[key] = i === 0 && j === 0 ? 1 : 0)));
      body = { updates };
    } else if (gifts.length && (!qualifies || !live || unpaid)) {
      const updates = {};
      for (const line of gifts) for (const key of line.dataset.keys.split(',')) updates[key] = 0;
      body = { updates };
      if (unpaid && qualifies && live) this.#giftDeclined(true);
    } else if (live && variant && qualifies && !hasGiftProduct && !this.#giftDeclined()) {
      path = 'cart/add.js';
      body = { items: [{ id: Number(variant), quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }] };
    }
    if (!body) return;

    this.#giftBusy = true;
    try {
      await this.#enqueue(async () => {
        const data = await this.#post(path, body);
        this.#apply(data.sections?.[SECTION], { source: path === 'cart/add.js' ? 'add' : 'change' });
      });
      if (path === 'cart/add.js') this.#announce('A free gift was added to your cart.');
    } catch (error) {
      /* no gift this time (sold out or offer over); the cart itself is unaffected */
    } finally {
      this.#giftBusy = false;
    }
  }

  /* ---------- upsells ---------- */

  async #upsellAdd(button) {
    button.classList.add('is-busy');
    try {
      await this.add([{ id: Number(button.dataset.gccAdd), quantity: 1, properties: { _gc_src: 'cart_upsell' } }], {
        open: false,
      });
      window.clarity?.('event', 'gc_cart_upsell_add');
    } catch (error) {
      /* notice already shown */
    } finally {
      button.classList.remove('is-busy');
    }
  }

  async #loadAiUpsells() {
    if (this.dataset.upsellSource !== 'ai') return;
    const card = this.querySelector('[data-gcc-upsell]');
    const lines = this.querySelectorAll(SETS);
    const newest = lines[lines.length - 1];
    if (!card || !newest) return;
    const productId = newest.dataset.productId;
    try {
      let recs = this.#recsCache.get(productId);
      if (!recs) {
        const response = await fetch(
          `${root()}recommendations/products?product_id=${productId}&limit=10&intent=related&section_id=gc-cart-upsells`
        );
        const text = await response.text();
        const json = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-gcc-recs]');
        recs = json ? JSON.parse(json.textContent) : [];
        this.#recsCache.set(productId, recs);
      }
      this.#renderAiRows(card, recs);
    } catch (error) {
      /* keep the server-rendered Smart rows */
    }
  }

  #renderAiRows(card, recs) {
    const gap = Number(card.dataset.gap);
    const count = Number(card.dataset.count) || 2;
    const lines = [...this.querySelectorAll('.gcc-line')];
    const inIds = new Set(lines.map((line) => line.dataset.productId));
    const inTitles = new Set(lines.map((line) => line.dataset.title));
    const seen = new Set();
    const usable = recs.filter((p) => {
      if (!p.available || !p.price || inIds.has(String(p.id)) || inTitles.has(p.title) || seen.has(p.title)) return false;
      seen.add(p.title);
      return true;
    });
    const unlocking = usable.filter((p) => p.price >= gap);
    const picks = [...unlocking, ...usable.filter((p) => p.price < gap).sort((a, b) => b.price - a.price)].slice(0, count);
    const rows = [...card.querySelectorAll('.gcc-up__row:not(.gcc-up__row--bundle)')];
    if (!picks.length || !rows.length) return;

    const template = rows[0];
    const list = card.querySelector('.gcc-up__list');
    const fragment = document.createDocumentFragment();
    const bundleRow = card.querySelector('.gcc-up__row--bundle');
    if (bundleRow) fragment.append(bundleRow);
    for (const p of picks) {
      const row = template.cloneNode(true);
      row.dataset.productId = p.id;
      row.querySelectorAll('a').forEach((a) => (a.href = p.url));
      row.querySelector('.gcc-up__title').textContent = p.title;
      row.querySelector('.gcc-up__price').textContent = p.price_html;
      const gift = row.querySelector('.gcc-up__gift');
      if (gift) gift.hidden = p.gift_box === false;
      const img = row.querySelector('img');
      if (img && p.image) {
        img.src = p.image;
        img.removeAttribute('srcset');
      }
      const unlocks = p.price >= gap;
      let note;
      if (unlocks && card.dataset.kind === 'percent') {
        const save = Math.round(((Number(card.dataset.subtotal) + p.price) * Number(card.dataset.percent)) / 100);
        note = card.dataset.notePercent.replace('[amount]', this.#money(save));
      } else if (unlocks) {
        note = card.dataset.noteShipping.replace('[name]', card.dataset.name);
      } else {
        note = card.dataset.noteCloser.replace('[amount]', p.price_html);
      }
      row.querySelector('.gcc-up__note').textContent = note;
      const button = row.querySelector('[data-gcc-add]');
      button.dataset.gccAdd = p.variant;
      button.dataset.gccUnlocks = String(unlocks);
      button.setAttribute('aria-label', `Add ${p.title} to cart`);
      fragment.append(row);
    }
    list.replaceChildren(fragment);
  }

  /* ---------- add-to-cart paths outside the drawer ---------- */

  #onAddFormSubmit = (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?(\?|$)/.test(action)) return;
    if (form.closest('product-form-component, quick-order-list, [data-gcq-form]') || form.target === '_blank') return;
    event.preventDefault();
    this.#addForm(form, event.submitter);
  };

  #onThemeCartUpdate = (event) => {
    const data = event.detail?.data || {};
    if (data.source === 'gc-cart' || data.didError) return;
    const isAdd = data.source === 'product-form-component' || event.constructor?.name === 'CartAddEvent';
    const html = data.sections?.[SECTION];
    if (html) {
      this.#apply(html, { source: isAdd ? 'add' : 'refresh' });
      if (isAdd) this.open();
      this.#syncAll(null);
    } else {
      this.refresh({ open: isAdd });
    }
  };

  #onLegacyCartUpdated = () => {
    if (Date.now() - this.#lastOwnUpdate < 500) return;
    this.refresh({ open: true });
  };

  /** Safety net: any other script or app that changes the cart refreshes the drawer. */
  #watchNetwork(signal) {
    if (!('PerformanceObserver' in window)) return;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const name = entry.name;
          if (name.includes(MARK)) continue;
          if (/\/discount\/[^/?]+/.test(name)) {
            this.refresh();
            continue;
          }
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') continue;
          if (!/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(name)) continue;
          const failed = entry.responseStatus >= 400;
          this.refresh({ open: /\/cart\/add/.test(name) && !failed });
        }
      });
      observer.observe({ type: 'resource', buffered: false });
      signal.addEventListener('abort', () => observer.disconnect());
    } catch (error) {
      /* unsupported */
    }
  }

  /* ---------- small helpers ---------- */

  #paintDelivery() {
    const holder = this.querySelector('[data-gcc-delivery]');
    const slot = holder?.querySelector('[data-gcc-date]');
    if (!slot) return;
    const days = Number(holder.dataset.days) || 3;
    const date = new Date();
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const dow = date.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    slot.textContent = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }

  #showAdding() {
    this.open();
    if (this.querySelector('.gcc-line--ghost')) return;
    const ghost = document.createElement('li');
    ghost.className = 'gcc-line gcc-line--ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.innerHTML =
      '<span class="gcc-line__media gcc-skel"></span><span class="gcc-line__info"><span class="gcc-skel gcc-skel--t"></span><span class="gcc-skel gcc-skel--s"></span></span>';
    const list = this.querySelector('[data-gcc-lines]');
    if (list) list.append(ghost);
    else this.querySelector('[data-gcc-part="main"]')?.prepend(ghost);
    this.#announce('Adding to cart…');
  }

  #clearAdding() {
    this.querySelectorAll('.gcc-line--ghost').forEach((ghost) => ghost.remove());
  }

  #eagerImages() {
    for (const img of this.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
  }

  #warmOnIntent() {
    const warm = () => this.#eagerImages();
    for (const opener of document.querySelectorAll('[data-gcc-open]')) {
      opener.addEventListener('pointerenter', warm, { once: true, passive: true });
      opener.addEventListener('touchstart', warm, { once: true, passive: true });
    }
  }

  #notice(message) {
    const el = this.querySelector('[data-gcc-notice]');
    if (!el) return;
    el.textContent = friendly(message);
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.hidden = true), 6000);
  }

  #announce(message) {
    const live = this.querySelector('[data-gcc-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => (live.textContent = message));
  }

  /** Formats cents in the cart currency exactly like Liquid's money filter, learned from a sample. */
  #money(cents) {
    if (!this.#fmt) {
      const sample = this.dataset.moneySample || '$1,234.56';
      const match = sample.match(/^(.*?)1(\D?)23[45](?:(\D)56)?(.*)$/);
      this.#fmt = match
        ? { pre: match[1], thousands: match[2], decimal: match[3] || '', post: match[4] }
        : { pre: '$', thousands: ',', decimal: '.', post: '' };
    }
    const { pre, thousands, decimal, post } = this.#fmt;
    const abs = Math.abs(cents);
    const units = decimal ? Math.floor(abs / 100) : Math.round(abs / 100);
    const whole = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = decimal ? decimal + String(abs % 100).padStart(2, '0') : '';
    return `${cents < 0 ? '−' : ''}${pre}${whole}${fraction}${post}`;
  }

}

if (!customElements.get('gc-cart-drawer')) customElements.define('gc-cart-drawer', GCCartDrawer);
